import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Box } from '@mui/material';
import UserApiService from 'api/UserApiService';
import { PropertyNameOptions, SelectTypeOptions } from 'components/Select';
import { useTranslation } from 'react-i18next';
import useApi from 'hooks/useApi';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import moment, { Moment } from 'moment';
import Order from 'types/entities/Order';
import User from 'types/entities/User';
import Scope from 'types/entities/Scope';
import TMOrderScope from 'types/entities/TMOrderScope';
import { updateIds } from 'helpers/orderhelper';
import { OrderType } from 'types/enums/OrderType';
import ScopeApiService from 'api/ScopeApiService';
import EngagementParaOrder from './EngagementParaOrder';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import { ActionDescriptor } from 'types/enums/ActionDescriptor';
import ChangeBaseLineAPIServices from 'api/ChangeBaseLineAPIServices';
import { injectOrdersAction } from 'store/actions/managementActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { selectPurchaseOrder } from 'store/selectors/deliverableSelectors';
import { loadCustomersAction, getbaseLineEditOrderAction } from 'store/actions/changeBaselineActions';
import { openDialogAction } from 'store/actions/dialogsActions';
import OrderActionRestrictionDialog from 'features/Orders/OrderDialogs/OrderActionRestrictionDialog';

export interface EngagementParametersRefType {
  handleSubmitEngagementPara: (fnName: string) => void;
}

interface EngagementParametersProps {
  selectedConsultants: any;
  setSelectedConsultants: React.Dispatch<React.SetStateAction<any>>;
  deliveryManagers: any;
  setDeliveryManagers: React.Dispatch<React.SetStateAction<any>>;
  selectedDeliveryManager: any;
  setSelectedDeliveryManager: React.Dispatch<React.SetStateAction<any>>;
  selectedScopes: Scope[];
  setSelectedScopes: React.Dispatch<React.SetStateAction<any>>;
  representatives: any;
  setRepresentatives: React.Dispatch<React.SetStateAction<any>>;
  selectedRepresentative: any;
  setSelectedRepresentative: React.Dispatch<React.SetStateAction<any>>;
  setIsCreating: React.Dispatch<React.SetStateAction<any>>;
  isCreating: boolean;
  currentStep: number;
  setActiveBaseFlag: React.Dispatch<React.SetStateAction<any>>;
  handleNext: React.Dispatch<React.SetStateAction<any>>;
  handleCancel: React.Dispatch<React.SetStateAction<any>>;
  savedFetchOrder: (fnName: boolean, baseline_version: number) => void;
  availableScopes?: Scope[];
  availableDeliveryManagers?: User[];
  availableConsultants?: User[];
  customerId: User['id'];
}

const EngagementParameters = forwardRef<EngagementParametersRefType, EngagementParametersProps>((props, ref) => {
  const {
    selectedConsultants,
    setSelectedConsultants,
    deliveryManagers,
    setDeliveryManagers,
    selectedDeliveryManager,
    setSelectedDeliveryManager,
    selectedScopes,
    setSelectedScopes,
    selectedRepresentative,
    setSelectedRepresentative,
    isCreating,
    setIsCreating,
    currentStep,
    setActiveBaseFlag,
    handleNext,
    handleCancel,
    savedFetchOrder,
    availableScopes,
    availableDeliveryManagers,
    availableConsultants,
    customerId,
  } = props;

  const [t] = useTranslation();
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  // const order = useSelector((state) => state?.changeBaseline?.sourceOrder);
  const { sourceOrder, baseLineOrder } = useSelector((state) => state.changeBaseline);

  const dialogData = useSelector((state) => state.dialogs);
  const purchaseOrder = useSelector((state) => state.purchaseOrder.datas);
  const [editOrder, setEditOrder] = useState<Partial<Order> | undefined>();
  const { makeCall } = useApi();

  const initialDialogData = {
    data: {
      order: baseLineOrder != null && baseLineOrder !== undefined ? baseLineOrder : sourceOrder,
    },
  };
  const restrictionDialogMessage = dialogData?.data?.message || '';

  // State variables
  const [initialDiaData, setInitialDiaData] = useState(initialDialogData);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [archivedScopes, setArchivedScopes] = useState<Scope[]>([]);
  const [clients, setClients] = useState<User[]>([]);
  const [consultants, setConsultants] = useState<User[]>([]);
  const [archivedConsultants, setArchivedConsultants] = useState<User[]>([]);
  const [archivedDeliveryManagers, setArchivedDeliveryManagers] = useState<User[]>([]);
  const [archivedRepresentatives, setArchivedRepresentatives] = useState<User[]>([]);
  const [representatives, setRepresentatives] = useState<User[]>([]);
  // Set edit order when order changes
  useEffect(() => {
    if (baseLineOrder === null) {
      setEditOrder(sourceOrder ?? undefined);
      dispatch(getbaseLineEditOrderAction(sourceOrder));
    } else {
      setEditOrder(baseLineOrder);
      dispatch(getbaseLineEditOrderAction(baseLineOrder));
    }
  }, [baseLineOrder]);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        // Get scopes
        if (customerId) {
          const scopesRetrieved = await ScopeApiService.fetchByCustomerId(customerId);
          setScopes(scopesRetrieved);
        }
        // Get creators
        const creatorsFetched = await UserApiService.fetchPossiblesDelegues();
        setRepresentatives(creatorsFetched);

        // Reset purchase orders
        // dispatch(getPurchaseOrderAction([]));
      } catch (error) {
        console.error('Error initializing data:', error);
      }
    };

    initializeData();
  }, [customerId, representatives.length]);

  // Fetch clients - Fixed implementation
  useEffect(() => {
    if (!customerId || clients.length > 0) return;
    const getClients = async () => {
      try {
        const clientsData = await makeCall(
          UserApiService.getClientsOnCustomer(customerId, {
            is_archived: false,
            size: '-1',
          }),
          'Failed to retrieve clients'
        );
        if (clientsData) {
          dispatch(loadCustomersAction(clientsData?.datas));
        }
        setClients(clientsData?.datas || []);
      } catch (error) {
        console.error('Error fetching clients:', error);
        setClients([]);
      }
    };
    getClients();
  }, [customerId, clients.length]);

  // Fetch consultants
  useEffect(() => {
    if (!customerId || consultants.length > 0) return;
    const getConsultants = async () => {
      try {
        const consultantsData = await makeCall(
          UserApiService.fetchPossiblesConsultants(),
          'Unable to retrieve consultants'
        );
        setConsultants(consultantsData || []);
      } catch (error) {
        console.error('Error fetching consultants:', error);
        setConsultants([]);
      }
    };
    getConsultants();
  }, [customerId, consultants.length]);

  // Fetch delivery managers
  useEffect(() => {
    if (!customerId || deliveryManagers.length > 0) return;
    const getDeliveryManagers = async () => {
      try {
        const deliveryManagersData = await makeCall(
          UserApiService.fetchPossiblesDeliverableTeamLeaders(),
          'Unable to retrieve delivery managers'
        );
        setDeliveryManagers(deliveryManagersData || []);
      } catch (error) {
        console.error('Error fetching delivery managers:', error);
        setDeliveryManagers([]);
      }
    };
    getDeliveryManagers();
  }, [customerId, deliveryManagers.length]);

  // Form handlers
  const handleChange = (e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.preventDefault();
    if (e.currentTarget.name === 'draft_reference' && e.currentTarget.value === '') {
      return setEditOrder({ ...editOrder, [e.currentTarget.name]: null });
    }
    dispatch(getbaseLineEditOrderAction({ ...editOrder, [e.currentTarget.name]: e.currentTarget.value }));
    return setEditOrder({ ...editOrder, [e.currentTarget.name]: e.currentTarget.value });
  };

  const handleSelectClient = (client: User | null | undefined) => {
    if (client) {
      dispatch(getbaseLineEditOrderAction({ ...editOrder, client, client_id: client.id }));
      return setEditOrder({ ...editOrder, client, client_id: client.id });
    }
    return setEditOrder({ ...editOrder, client: undefined, client_id: undefined });
  };

  // const handleSelectRepresentative = (representative: User | null | undefined) => {
  //   if (representative) return setEditOrder({ ...editOrder, delegue: representative, delegue_id: representative.id });
  //   return setEditOrder({ ...editOrder, delegue: undefined, delegue_id: undefined });
  // };

  const handleSelectOptions = (e: SelectTypeOptions | null, property: PropertyNameOptions) => {
    if (e && property === 'affair') {
      return setEditOrder({ ...editOrder, [`${property}`]: e, [`${property}_id`]: e.id });
    }
    return setEditOrder({ ...editOrder, affair: undefined, affair_id: undefined });
  };

  const handleDateChange = (name: string, date: Moment | null) => {
    const formattedDate = date?.format('YYYY-MM-DD');
    dispatch(getbaseLineEditOrderAction({ ...editOrder, [name]: formattedDate }));
    setEditOrder({ ...editOrder, [name]: formattedDate });
  };

  // Expose child function to parent
  useImperativeHandle(ref, () => ({
    handleSubmitEngagementPara,
  }));

  const handleSubmitEngagementPara = useCallback(
    async (fnName) => {
      if (isCreating || !editOrder) {
        console.log('Creation already in progress or no order data');
        return;
      }

      setIsCreating(true);
      try {
        const { id, affair, client, ...orderToSave } = editOrder as Order;

        const { ids: accountable_ids, archived: is_accountable_archived } = updateIds(
          selectedDeliveryManager,
          archivedDeliveryManagers
        );

        const { ids: consultant_ids, archived: is_consultant_archived } = updateIds(
          selectedConsultants,
          archivedConsultants
        );

        const { ids: representative_ids, archived: is_representative_archived } = updateIds(
          selectedRepresentative,
          archivedRepresentatives
        );

        const { ids: scope_ids, archived: is_scope_archived } = updateIds(selectedScopes, archivedScopes);

        const client_ids = client?.id ? [client.id] : null;
        const is_client_archived = client_ids !== null ? [false] : [];

        const tmOrderScope: TMOrderScope = {
          id: 0,
          accountable_ids,
          consultant_ids,
          delegue_ids: representative_ids,
          client_ids,
          scope_ids,
          order_id: orderToSave?.id,
          is_scope_archived,
          is_accountable_archived,
          is_consultant_archived,
          is_client_archived,
          is_delegue_archived: is_representative_archived,
        };

        const payloadDate = {
          ...orderToSave,
          baseline_version:
            sourceOrder?.baseline_version === 0 || sourceOrder?.baseline_version === null
              ? 1
              : Number(sourceOrder?.baseline_version) + 1,
          status: 4,
          is_baselined: 1,
          // client_id: sourceOrder?.client_id,
          source_order_id: sourceOrder?.id,
          order_baseline_id: baseLineOrder?.id ?? 0,
          customer_id: customerId,
          affair_name: affair?.name || null,
          affair_otp: affair?.otp || null,
          affair_id: affair?.id || null,
          creator_id: appState.user?.id,
          order_type_id: OrderType.CATALOG,
          page_typeid: currentStep,
          tm_order_scope: tmOrderScope,
          purchase_orders: purchaseOrder,
          action_id: ActionDescriptor.CreateCatalogOrder,
        };

        await makeCall(
          ChangeBaseLineAPIServices.createEngagementparameter(payloadDate),
          'Unable to change baseline order updated'
        );

        dispatch(getPurchaseOrderAction([]));

        if (fnName === 'save') {
          await savedFetchOrder(true, Number(sourceOrder?.baseline_version));
          // setBaselineOrderData(baseline_order?.baseline_order);
          localStorage.setItem('activeBaseFlag', JSON.stringify('NewBaseline'));
          setActiveBaseFlag('NewBaseline');
          dispatch(setSnackbarAction({ message: 'Saved', open: true, severity: 'success', duration: 500 }));
          handleNext();
        } else if (fnName === 'saveAaDraft') {
          handleCancel();
        }
      } catch (error) {
        console.error('❌ Failed to create baseline:', error);
        const result = (error as Error).message;
        dispatch(
          setSnackbarAction({
            message: `Fetch change baseline failed : ${result || error}`,
            open: true,
            severity: 'error',
          })
        );
      } finally {
        setIsCreating(false);
        // dispatch(getPurchaseOrderAction([]));
      }
    },
    [
      isCreating,
      editOrder,
      selectedDeliveryManager,
      archivedDeliveryManagers,
      selectedConsultants,
      archivedConsultants,
      selectedScopes,
      archivedScopes,
      selectedRepresentative,
      archivedRepresentatives,
      customerId,
      appState.user?.id,
      currentStep,
      purchaseOrder,
      makeCall,
      dispatch,
      setActiveBaseFlag,
    ]
  );

  const handleSwitchSign = (value: boolean) => {
    setEditOrder({ ...editOrder, is_sign_mandatory: value });
  };

  const handleClientConnectionSwitch = (value: boolean) => {
    setEditOrder({ ...editOrder, client_connection: value });
  };

  const handleScopeAdded = (newScope: Scope) => {
    const scopeWithArchiveStatus = { ...newScope, is_archived: false };
    setScopes((prevScopes) => [...prevScopes, scopeWithArchiveStatus]);
  };

  const handleScopeArchived = (updatedScope: Scope) => {
    setScopes((prevScopes) =>
      prevScopes.map((scope) => (scope.id === updatedScope.id ? { ...scope, is_archived: true } : scope))
    );
    const deliverable_scope = initialDialogData?.data.order?.scopes;
    const existsById = deliverable_scope?.some((item) => item.id === updatedScope.id);
    if (!existsById) {
      setArchivedScopes((prevArchivedScopes) => [...prevArchivedScopes, updatedScope]);
    }
  };

  const handleScopesChange = React.useCallback(
    (newSelectedScopes: Scope[]) => {
      setSelectedScopes(newSelectedScopes);
    },
    [setSelectedScopes]
  );

  const handleConsultantChange = (newSelectedConsultants: User[], reason?: string) => {
    setSelectedConsultants(newSelectedConsultants);
  };

  const handleConsultantArchived = (updatedUser: User) => {
    setArchivedConsultants((prevArchiveConsultant) => [...prevArchiveConsultant, updatedUser]);
  };

  const handleDeliveryManagerChange = (newSelectedDeliveryManager: User[], reason?: string) => {
    setSelectedDeliveryManager(newSelectedDeliveryManager);
  };

  const handleDeliveryManagerArchived = (updatedUser: User) => {
    setArchivedDeliveryManagers((prevArchivedDeliveryManager) => [...prevArchivedDeliveryManager, updatedUser]);
  };

  const handleRepresentativeChange = (newSelectedRepresentative: User[], reason?: string) => {
    setSelectedRepresentative(newSelectedRepresentative);
  };

  const handleRepresentativeArchived = (updatedUser: User) => {
    setArchivedRepresentatives((prevArchivedRepresentatives) => [...prevArchivedRepresentatives, updatedUser]);
  };

  const handleScopeDeletionMessage = useCallback(() => {
    setInitialDiaData((prev) => ({
      ...prev,
      data: {
        order: prev.data?.order,
      },
    }));

    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message: 'You are unable to perform this action. This scope is already associated with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  const handleDMUserDeletionMessage = useCallback(() => {
    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message:
            'You are unable to perform this action. This delivery manager is already linked with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  const handleConsultantUserDeletionMessage = useCallback(() => {
    dispatch(
      openDialogAction({
        name: 'orderActionRestrictionDialog',
        data: {
          message:
            'You are unable to perform this action. This consultant is already linked with ongoing deliverables.',
        },
      })
    );
  }, [dispatch]);

  return (
    <Box sx={{ mb: 5 }}>
      {editOrder && (
        <EngagementParaOrder
          handleChange={handleChange}
          handleSelectClient={handleSelectClient}
          handleSelectOptions={handleSelectOptions}
          handleDateChange={handleDateChange}
          handleSwitchSign={handleSwitchSign}
          handleClientConnectionSwitch={handleClientConnectionSwitch}
          order={editOrder}
          // creators={creators}
          clients={clients}
          scopes={scopes}
          consultants={consultants}
          deliveryManagers={deliveryManagers}
          representatives={representatives}
          onScopeAdded={handleScopeAdded}
          onScopeArchived={handleScopeArchived}
          handleScopesChange={handleScopesChange}
          onConsultantArchived={handleConsultantArchived}
          handleConsultantChange={handleConsultantChange}
          onDeliveryManagerArchived={handleDeliveryManagerArchived}
          handleDeliveryManagerChange={handleDeliveryManagerChange}
          onScopeDeletionAttempt={handleScopeDeletionMessage}
          onDMUserDeletionAttempt={handleDMUserDeletionMessage}
          onConsultantUserDeletionAttempt={handleConsultantUserDeletionMessage}
          onRepresentativeArchived={handleRepresentativeArchived}
          handleRepresentativeChange={handleRepresentativeChange}
          onRepresentativeUserDeletionAttempt={() => {
            // intentionally left blank
          }}
          selectedRepresentative={selectedRepresentative}
          selectedScopes={selectedScopes}
          selectedConsultants={selectedConsultants}
          selectedDeliveryManager={selectedDeliveryManager}
          availableScopes={availableScopes}
          availableDeliveryManagers={availableDeliveryManagers}
          availableConsultants={availableConsultants}
          variant="filled"
        />
      )}

      <OrderActionRestrictionDialog message={restrictionDialogMessage} />
    </Box>
  );
});

export default EngagementParameters;
